import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/lib/auth';
import { HelpCircle, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Definizione degli step per ogni ruolo
const getStepsForRole = (ruolo) => {
  const aziendaSteps = [
    {
      target: '[data-testid="sidebar"]',
      title: 'Benvenuto!',
      content: 'Questa è la barra di navigazione. Da qui puoi accedere a tutte le sezioni dell\'applicazione.',
    },
    {
      target: '[data-testid="dashboard-stats"]',
      title: 'Dashboard',
      content: 'Qui trovi le statistiche principali: elogi creati, QR code attivi e quelli in attesa di pubblicazione.',
    },
    {
      target: '[data-testid="new-elogio-btn"]',
      title: 'Nuovo Elogio',
      content: 'Clicca qui per creare un nuovo elogio. Inserisci i dati del defunto e genera automaticamente il testo con l\'AI.',
    },
    {
      target: '[data-testid="nav-elogi"]',
      title: 'Sezione Elogi',
      content: 'Nella sezione Elogi trovi tutti gli elogi creati. Puoi modificarli, aggiungere foto alla galleria e pubblicarli.',
    },
    {
      target: '[data-testid="nav-qr-code"]',
      title: 'QR Code',
      content: 'Qui trovi i QR Code assegnati alla tua azienda. Dopo la pubblicazione, potrai scaricare il QR da stampare.',
    },
  ];

  const adminSteps = [
    {
      target: '[data-testid="sidebar"]',
      title: 'Benvenuto Admin!',
      content: 'Questa è la barra di navigazione. Da qui puoi gestire tutto il sistema.',
    },
    {
      target: '[data-testid="dashboard-stats"]',
      title: 'Dashboard',
      content: 'Dashboard amministrativa con statistiche globali: aziende, agenti, elogi e QR code.',
    },
    {
      target: '[data-testid="nav-aziende"]',
      title: 'Gestione Aziende',
      content: 'Gestisci le aziende iscritte. Puoi creare nuove aziende, assegnare QR code e inviare email di benvenuto.',
    },
    {
      target: '[data-testid="nav-agenti"]',
      title: 'Gestione Agenti',
      content: 'Gestisci gli agenti commerciali. Puoi vedere le loro aziende e gli elogi demo creati.',
    },
    {
      target: '[data-testid="nav-elogi"]',
      title: 'Tutti gli Elogi',
      content: 'Visualizza tutti gli elogi del sistema. Come admin puoi anche attivarli gratuitamente.',
    },
    {
      target: '[data-testid="nav-impostazioni"]',
      title: 'Impostazioni',
      content: 'Configura il sistema: pagamenti, integrazioni, email e pulizia database.',
    },
  ];

  const agenteSteps = [
    {
      target: '[data-testid="sidebar"]',
      title: 'Benvenuto Agente!',
      content: 'Questa è la barra di navigazione. Da qui puoi accedere alle tue sezioni.',
    },
    {
      target: '[data-testid="dashboard-stats"]',
      title: 'Dashboard',
      content: 'Qui vedi le statistiche delle tue aziende: quante ne gestisci e quanti elogi hanno creato.',
    },
    {
      target: '[data-testid="nav-le-tue-aziende"]',
      title: 'Le tue Aziende',
      content: 'Visualizza le aziende che ti sono state assegnate e i loro progressi.',
    },
    {
      target: '[data-testid="nav-elogi-demo"]',
      title: 'Elogi Demo',
      content: 'Qui puoi creare elogi demo per mostrare ai tuoi clienti come funziona il servizio. Si cancellano automaticamente dopo 3 giorni.',
    },
  ];

  switch (ruolo) {
    case 'admin':
      return adminSteps;
    case 'agente':
      return agenteSteps;
    case 'azienda':
    default:
      return aziendaSteps;
  }
};

const TOUR_STORAGE_KEY = 'rememberme_tour_completed';

const GuidedTour = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState([]);
  const [targetRect, setTargetRect] = useState(null);

  const updateTargetPosition = useCallback(() => {
    if (!steps[currentStep]) return;
    
    const target = document.querySelector(steps[currentStep].target);
    if (target) {
      const rect = target.getBoundingClientRect();
      setTargetRect(rect);
    } else {
      setTargetRect(null);
    }
  }, [currentStep, steps]);

  useEffect(() => {
    if (user) {
      const tourCompleted = localStorage.getItem(`${TOUR_STORAGE_KEY}_${user.id}`);
      
      if (!tourCompleted) {
        const timer = setTimeout(() => {
          setSteps(getStepsForRole(user.ruolo));
          setIsOpen(true);
        }, 1500);
        return () => clearTimeout(timer);
      }
    }
  }, [user]);

  useEffect(() => {
    if (isOpen) {
      updateTargetPosition();
      window.addEventListener('resize', updateTargetPosition);
      window.addEventListener('scroll', updateTargetPosition);
      return () => {
        window.removeEventListener('resize', updateTargetPosition);
        window.removeEventListener('scroll', updateTargetPosition);
      };
    }
  }, [isOpen, updateTargetPosition]);

  const handleClose = () => {
    setIsOpen(false);
    setCurrentStep(0);
    if (user) {
      localStorage.setItem(`${TOUR_STORAGE_KEY}_${user.id}`, 'true');
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const restartTour = () => {
    if (user) {
      setSteps(getStepsForRole(user.ruolo));
      setCurrentStep(0);
      setIsOpen(true);
    }
  };

  if (!user) return null;

  const currentStepData = steps[currentStep];

  return (
    <>
      {/* Overlay e Tooltip */}
      {isOpen && currentStepData && (
        <div className="fixed inset-0 z-[9999]">
          {/* Overlay scuro */}
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={handleClose}
          />
          
          {/* Highlight dell'elemento target */}
          {targetRect && (
            <div
              className="absolute bg-transparent border-2 border-white rounded-lg shadow-lg pointer-events-none"
              style={{
                top: targetRect.top - 4,
                left: targetRect.left - 4,
                width: targetRect.width + 8,
                height: targetRect.height + 8,
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
              }}
            />
          )}
          
          {/* Tooltip */}
          <div 
            className="absolute bg-white rounded-lg shadow-2xl p-5 max-w-sm z-10"
            style={{
              top: targetRect 
                ? Math.min(targetRect.bottom + 16, window.innerHeight - 200)
                : '50%',
              left: targetRect 
                ? Math.min(Math.max(targetRect.left, 16), window.innerWidth - 340)
                : '50%',
              transform: targetRect ? 'none' : 'translate(-50%, -50%)',
            }}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-3">
              <h3 className="font-bold text-lg text-slate-900">
                {currentStepData.title}
              </h3>
              <button 
                onClick={handleClose}
                className="text-slate-400 hover:text-slate-600 p-1"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {/* Content */}
            <p className="text-slate-600 text-sm leading-relaxed mb-4">
              {currentStepData.content}
            </p>
            
            {/* Footer */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-slate-400">
                {currentStep + 1} di {steps.length}
              </span>
              <div className="flex gap-2">
                {currentStep > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handlePrev}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Indietro
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={handleNext}
                  className="bg-slate-900 hover:bg-slate-800"
                >
                  {currentStep === steps.length - 1 ? 'Fine' : 'Avanti'}
                  {currentStep < steps.length - 1 && <ChevronRight className="h-4 w-4 ml-1" />}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Pulsante per rivedere il tour */}
      <Button
        variant="ghost"
        size="sm"
        onClick={restartTour}
        className="fixed bottom-4 right-4 z-50 bg-white shadow-lg hover:bg-slate-100 text-slate-600 border"
        title="Rivedi il tour guidato"
      >
        <HelpCircle className="h-5 w-5 sm:mr-2" />
        <span className="hidden sm:inline">Guida</span>
      </Button>
    </>
  );
};

export default GuidedTour;
